import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import {
  BankCashAccountSchema,
  CashPositionSchema,
  type AccountDto,
  type BankCashAccountDto,
} from '@taqon/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { PageHeader } from '../../shared/ui/page';
import { AccountPicker } from './lookups';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { Movements } from './BankMovements';

/**
 * Bank & Cash registers, their balances and the consolidated cash position (FRD-FIN-091, FIN-093), with the register create and
 * change the existing API offers, and the movements of every register or of the one chosen
 * (`?register=<id>`, so a refresh keeps it), where deposits and withdrawals are recorded.
 *
 * The balance is derived from posted movements, so it moves when a payment posts and needs no
 * separate refresh path beyond invalidating the `bank-cash` key.
 *
 * Every amount is AED: the model defines no currency attribute (D5), and no foreign-currency or
 * FX account is configured while CLR-09 is open. Statement import and reconciliation are Phase 2.
 */
export function BankCashPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'finance.bank_cash_account:create');
  const canChange = hasCapability(session.data, 'finance.bank_cash_account:change');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BankCashAccountDto | null>(null);
  const canSeeMovements = hasCapability(session.data, 'finance.bank_cash_transaction:view');
  const [params, setParams] = useSearchParams();
  const registerId = params.get('register') ?? '';
  const chooseRegister = (id: string) => {
    setParams(id === '' ? {} : { register: id }, { replace: true });
  };
  const accounts = useQuery({
    queryKey: ['bank-cash'],
    queryFn: () => getPage('/api/v1/finance/bank-cash-accounts?pageSize=50', BankCashAccountSchema),
  });

  // The consolidated cash position (FRD-FIN-093): each register's balance and the total, both
  // derived by the API from posted movements. The page adds nothing up itself.
  const position = useQuery({
    queryKey: ['bank-cash', 'position'],
    queryFn: () => getData('/api/v1/finance/cash-position', CashPositionSchema),
  });

  const balanceOf = (id: string): string => {
    if (position.isPending) return '…';
    if (position.isError) return '—';
    return position.data.registers.find((r) => r.id === id)?.balance ?? '—';
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Bank & cash"
        description="Balances are derived from posted movements. All amounts are in AED."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreating(true);
              }}
            >
              New register
            </Button>
          ) : undefined
        }
      />

      {accounts.isError && <Alert severity="error">{accounts.error.message}</Alert>}
      {accounts.isPending && <CircularProgress aria-label="Loading bank and cash accounts" />}

      {accounts.data !== undefined && (
        <Paper sx={{ p: 2, overflowX: 'auto' }}>
          <Table size="small" aria-label="Bank and cash accounts">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Balance</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.data.items.map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {canSeeMovements ? (
                      <Button
                        size="small"
                        sx={{ fontWeight: 600, p: 0, minWidth: 0 }}
                        aria-label={`Show the movements of ${account.code}`}
                        onClick={() => {
                          chooseRegister(account.id);
                        }}
                      >
                        {account.code}
                      </Button>
                    ) : (
                      account.code
                    )}
                  </TableCell>
                  <TableCell>{account.name}</TableCell>
                  <TableCell>{account.accountKind.replace('_', ' ')}</TableCell>
                  <TableCell>
                    <StatusChip status={account.status} />
                  </TableCell>
                  <TableCell align="right">{balanceOf(account.id)}</TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.currentTarget.focus();
                          setEditing(account);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {accounts.data.items.length > 0 && (
                <TableRow>
                  <TableCell colSpan={4} component="th" scope="row" sx={{ fontWeight: 700 }}>
                    Consolidated cash position
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700 }}
                    aria-label="Cash position total"
                  >
                    {position.data === undefined ? '…' : `AED ${position.data.total}`}
                  </TableCell>
                  {canChange && <TableCell />}
                </TableRow>
              )}
              {accounts.data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canChange ? 6 : 5}>
                    No bank or cash registers yet. They are configured after the Chart of Accounts
                    is imported.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
      {canSeeMovements && accounts.data !== undefined && (
        <Movements
          registers={accounts.data.items}
          registerId={registerId}
          onRegisterChange={chooseRegister}
        />
      )}
      <CreateRegisterDialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
      />
      {editing !== null && (
        <EditRegisterDialog
          key={`${editing.id}:${String(editing.version)}`}
          register={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Stack>
  );
}

const KINDS = [
  ['bank', 'Bank account'],
  ['cash_on_hand', 'Cash on hand'],
  ['petty_cash', 'Petty cash'],
] as const;

/**
 * A new register over a COA posting account (ERD §5.11). A bank register carries a bank name and
 * account identifier; a cash register carries neither — the API enforces it, the form follows it.
 */
function CreateRegisterDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('bank');
  const [bankName, setBankName] = useState('');
  const [identifier, setIdentifier] = useState('');
  // The whole COA is searchable on the server (FRD-FIN-091): no first-page limit.
  const [glAccount, setGlAccount] = useState<AccountDto | null>(null);
  const glAccountId = glAccount?.id ?? '';
  const bank = kind === 'bank';
  const create = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/finance/bank-cash-accounts',
        {
          code: code.trim(),
          name: name.trim(),
          accountKind: kind,
          ...(bank ? { bankName: bankName.trim(), bankAccountIdentifier: identifier.trim() } : {}),
          glAccountId,
        },
        BankCashAccountSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setCode('');
      setName('');
      setBankName('');
      setIdentifier('');
      setGlAccount(null);
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['bank-cash'] });
    },
  });
  const canSubmit =
    code.trim() !== '' &&
    name.trim() !== '' &&
    glAccountId !== '' &&
    (!bank || (bankName.trim() !== '' && identifier.trim() !== ''));
  return (
    <FormDialog
      open={open}
      title="New bank or cash register"
      submitLabel="Create register"
      canSubmit={canSubmit}
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={() => {
        create.reset();
        onClose();
      }}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Code"
        required
        autoFocus
        value={code}
        onChange={(e) => {
          setCode(e.target.value.toUpperCase());
        }}
        helperText="Upper case, up to 20 characters, for example BANK-MAIN."
      />
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        select
        label="Kind"
        value={kind}
        onChange={(e) => {
          setKind(e.target.value);
        }}
      >
        {KINDS.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {bank && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Bank name"
            required
            fullWidth
            value={bankName}
            onChange={(e) => {
              setBankName(e.target.value);
            }}
          />
          <TextField
            label="Account identifier"
            required
            fullWidth
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
            }}
          />
        </Stack>
      )}
      <AccountPicker
        value={glAccount}
        onChange={setGlAccount}
        helperText="The Chart of Accounts posting account this register stands for (search by code or name)."
      />
    </FormDialog>
  );
}

/**
 * A change with If-Match. The account identifier is never pre-filled or echoed back: it may be a
 * masked bank detail, so it is sent only when a new one is typed.
 */
function EditRegisterDialog({
  register,
  onClose,
}: {
  register: BankCashAccountDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const bank = register.accountKind === 'bank';
  const [name, setName] = useState(register.name);
  const [bankName, setBankName] = useState(register.bankName ?? '');
  const [identifier, setIdentifier] = useState('');
  const [reconciliation, setReconciliation] = useState(register.reconciliationStatus);
  const [status, setStatus] = useState(register.status);
  // Only what changed is sent (the API refuses a no-op change).
  const changes: Record<string, string> = {};
  if (name.trim() !== register.name) changes.name = name.trim();
  if (reconciliation !== register.reconciliationStatus)
    changes.reconciliationStatus = reconciliation;
  if (status !== register.status) changes.status = status;
  if (bank && bankName.trim() !== '' && bankName.trim() !== (register.bankName ?? '')) {
    changes.bankName = bankName.trim();
  }
  if (bank && identifier.trim() !== '') changes.bankAccountIdentifier = identifier.trim();
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `/api/v1/finance/bank-cash-accounts/${register.id}`,
        changes,
        BankCashAccountSchema,
        session.data?.csrfToken ?? '',
        register.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['bank-cash'] });
    },
  });
  return (
    <FormDialog
      open
      title={`Edit register ${register.code}`}
      submitLabel="Save changes"
      canSubmit={name.trim() !== '' && Object.keys(changes).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      {bank && (
        <>
          <TextField
            label="Bank name"
            value={bankName}
            onChange={(e) => {
              setBankName(e.target.value);
            }}
          />
          <TextField
            label="New account identifier"
            value={identifier}
            onChange={(e) => {
              setIdentifier(e.target.value);
            }}
            helperText="Leave empty to keep the current identifier."
          />
        </>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          fullWidth
          label="Reconciliation"
          value={reconciliation}
          onChange={(e) => {
            setReconciliation(e.target.value);
          }}
        >
          <MenuItem value="unreconciled">Unreconciled</MenuItem>
          <MenuItem value="reconciled">Reconciled</MenuItem>
        </TextField>
        <TextField
          select
          fullWidth
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
          }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Stack>
    </FormDialog>
  );
}
